import React from 'react';
import { X } from 'lucide-react';

interface TermsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function TermsModal({ isOpen, onClose }: TermsModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full p-6 my-8 max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-start">
          <h2 className="text-2xl font-bold text-gray-900">
            Termeni și Condiții
          </h2>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <div className="mt-6 overflow-y-auto flex-1 prose prose-slate max-w-none">
          <section>
            <h3 className="text-xl font-semibold text-gray-900">1. Informații generale</h3>
            <p>
              Platforma decizieoncologica.ro este operată de ASOCIAȚIA MEDICALĂ MULTIDISCIPLINARĂ, cu sediul în Cluj-Napoca, Cluj, România, C.I.F.: 51317071. Comisia medicală organizată de către ASOCIAȚIA MEDICALĂ MULTIDISCIPLINARĂ pe baza contractelor de colaborare cu medicii participanți și pe baza avizelor anuale privind exercitarea profesiei de medic. Prețul unei consultații este stabilit la 1.000 lei.
            </p>
          </section>

          <section className="mt-6">
            <h3 className="text-xl font-semibold text-gray-900">2. Utilizarea platformei</h3>
            <p>
              Prin accesarea și utilizarea platformei noastre, sunteți de acord să respectați următorii termeni și condiții.
            </p>
          </section>

          <section className="mt-6">
            <h3 className="text-xl font-semibold text-gray-900">3. Programări și livrarea rapoartelor medicale</h3>
            <p>
              Termenul standard de livrare a raportului medical este de 10 zile lucrătoare. Acest termen poate fi influențat de disponibilitatea medicilor specialiști invitați în comisie. Ne străduim să respectăm termenele stabilite, dar nu garantăm livrarea în termenul menționat.
            </p>
          </section>

          <section className="mt-6">
            <h3 className="text-xl font-semibold text-gray-900">4. Exonerarea de răspundere</h3>
            <p>
              Utilizați platforma pe propria răspundere. ASOCIAȚIA MEDICALĂ MULTIDISCIPLINARĂ nu este responsabilă pentru pierderea datelor sau pentru eventualele erori ori omisiuni în redactarea sau prezentarea materialelor și informațiilor de pe site. Nu oferim nicio garanție referitoare la conținutul și utilizarea acestui site. Informațiile publicate corespund realității la momentul înscrierii lor pe site sau al actualizării paginilor.
            </p>
          </section>

          <section className="mt-6">
            <h3 className="text-xl font-semibold text-gray-900">5. Politica de retur</h3>
            <p>
              Pacientul poate solicita rambursarea contravalorii serviciului plătit la adresa admin@decizieoncologica.ro în termen de 10 zile lucrătoare de la achiziție. Suma va fi returnată în termen de 30 de zile de la efectuarea solicitării.
            </p>
          </section>

          <section className="mt-6">
            <h3 className="text-xl font-semibold text-gray-900">6. Politica de confidențialitate și GDPR</h3>
            <p>
              Pentru a vă oferi cele mai bune servicii, ASOCIAȚIA MEDICALĂ MULTIDISCIPLINARĂ procesează date cu caracter personal în conformitate cu Regulamentul General privind Protecția Datelor (GDPR). Prin utilizarea platformei noastre, sunteți de acord cu:
            </p>
            <ul className="list-disc pl-6 mt-2 space-y-2">
              <li>Colectarea și procesarea datelor medicale pentru furnizarea serviciilor medicale.</li>
              <li>Stocarea securizată a documentelor medicale încărcate.</li>
              <li>Transmiterea informațiilor către echipa medicală pentru analiză.</li>
              <li>Utilizarea datelor de contact pentru comunicări legate de serviciile medicale.</li>
            </ul>
          </section>

          <section className="mt-6">
            <h3 className="text-xl font-semibold text-gray-900">7. Procesarea plăților online</h3>
            <p>
              Pentru a vă oferi opțiunea plății online, folosim platforma de procesare plăți NETOPIA PAYMENTS.
              Atunci când optați pentru plata online, veți fi redirecționat către platforma NETOPIA PAYMENTS, în
              care vi se vor solicita anumite date personale în funcție de metoda de plată aleasă - spre exemplu,
              datele de card (numărul cardului, numele & prenumele deținătorului cardului, data expirării și codul de
              securitate de pe spatele cardului). Aceste date sunt folosite strict pentru procesarea plății prin
              intermediul platformei NETOPIA PAYMENTS și nu ne sunt comunicate.
            </p>
            <p>
              În anumite situații, putem transmite direct către platforma NETOPIA PAYMENTS o serie de informații
              cu privire la tranzacție, anume: valoarea tranzacției, număr de telefon si adresa e-mail (dacă acestea
              au fost completate în contul clientului).
            </p>
            <p>
              Pentru a putea asigura urmărirea tranzacțiilor și decontarea plăților, NETOPIA PAYMENTS ne
              furnizează rapoarte cu privire la plățile pe care le încasăm. Aceste rapoarte pot includ date personale
              ale plătitorilor (nume, prenume, e-mail, nr. telefon, adresa billing). NETOPIA PAYMENTS ne transmite
              aceste date ca parte a serviciilor de procesare a plăților.
            </p>
            <p>
              În cazul în care apar probleme legate strict de procesarea plății dvs., veți putea fi contactat în mod
              direct de către reprezentanții NETOPIA PAYMENTS pentru soluționarea acestora.
            </p>
            <p>
              Platforma NETOPIA PAYMENTS prelucrează datele dvs. personale în calitate de operator
              independent pentru procesarea plății online. Puteți consulta mai multe detalii despre politica acestora
              de prelucrare a datelor dvs. personale colectate în vederea procesării plății{' '}
              <a 
                href="https://netopia-payments.com/nota-de-informare-gdpr-platitori/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#007FFF] hover:text-[#024994]"
              >
                aici
              </a>.
            </p>
          </section>

          <section className="mt-6">
            <h3 className="text-xl font-semibold text-gray-900">8. Consimțământ medical informat</h3>
            <p className="mt-2">
              Această secțiune reprezintă exprimarea acordului informat al pacientului pentru prestarea unui act medical non-invaziv, în regim de telemedicină, sub forma teleexpertizei (art. 30² lit. b din Legea nr. 95/2006).
            </p>
            
            <p className="mt-4">
              Consult medical de tip „second opinion” realizat la distanță, în regim de telemedicină, de către o comisie multidisciplinară alcătuită din medici specialiști autorizați, care evaluează cazul medical prezentat, pe baza documentației furnizate de pacient.
            </p>

            <ul className="list-disc pl-6 mt-4 space-y-2">
              <li>Acest serviciu se încadrează la teleexpertiză – „activitatea desfășurată de unul sau mai mulți profesioniști din domeniul sănătății în scopul formulării unei opinii medicale” – conform art. 30² lit. b) din Legea nr. 95/2006</li>
              <li>Comisia medicală este formată din medici specialiști în: oncologie medicală, radioterapie, chirurgie oncologică, radiologie și imagistică medicală, medicină de laborator și anatomopatologie</li>
              <li>Consultul second opinion este realizat exclusiv la distanță prin platformă de telemedicină securizată</li>
              <li>Nu implică examinare fizică directă - se bazează pe documentația medicală furnizată</li>
              <li>Caracter orientativ și consultativ - nu înlocuiește actul medical clinic</li>
              <li>Pacientul poate solicita explicații suplimentare oricând</li>
              <li>Concluziile pot oferi opinii diferite față de medicul curant</li>
              <li>Recomandările sunt documentate în scrisoare medicală</li>
              <li>Pacientul poate accepta, refuza sau cere opinie medicală nouă</li>
              <li>Limitări inerente telemedicinei: imposibilitatea evaluării fizice directe</li>
            </ul>
          </section>

          <section className="mt-6">
            <h3 className="text-xl font-semibold text-gray-900">9. Modificarea termenilor și condițiilor</h3>
            <p>
              Ne rezervăm dreptul de a modifica acești termeni și condiții fără o notificare prealabilă. Vă recomandăm să verificați periodic această pagină pentru a fi la curent cu eventualele modificări.
            </p>
          </section>

          <section className="mt-6">
            <h3 className="text-xl font-semibold text-gray-900">10. Contact</h3>
            <p>
              Pentru orice întrebări sau nelămuriri, ne puteți contacta la adresa de email: contact@decizieoncologica.ro
               </p>
            <p>
              Pentru probleme de funcționare ale platformei ne puteți contacta la adresa de email: admin@decizieoncologica.ro
            </p>
          </section>

          <p className="mt-8 text-gray-600">
            Vă mulțumim că ați ales platforma decizieoncologica.ro pentru nevoile dumneavoastră medicale.
          </p>
        </div>

        <div className="mt-6 pt-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-[#007FFF] text-white rounded-lg hover:bg-[#024994] transition"
          >
            Închide
          </button>
        </div>
      </div>
    </div>
  );
}
